import { maxLength, minLength, numeric, required } from '@reliance/forms-js/lib/validation';
import { Facility } from '@reliance/types/models/Facility';
import React from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { FormRenderProps } from 'react-final-form';
import ErrorMessage from '../common/ErrorMessage';
import config from '../common/config';
import Checkbox from '../form/Checkbox';
import { Select } from '../form/Select';
import { States } from '../form/States';
import { TextInput } from '../form/TextInput';
import { useRatePlans } from '../hooks/Rate';
import { useRatePlans as useServiceRatePlans } from '../hooks/ServiceRate';
import { IUser } from '../models/auth';
import FeatureCheckboxes from './FeatureCheckboxes';

type AddEditFormProps = {
  form: FormRenderProps;
  user: IUser;
  facility?: Facility;
};

const AddEditForm: React.FC<AddEditFormProps> = (props) => {
  const ratePlans = useRatePlans();
  const serviceRatePlans = useServiceRatePlans();

  const { user } = props;
  const { values, submitError, form } = props.form;

  return (
    <>
      {submitError &&
        <ErrorMessage
          error={submitError}
          httpGenericMessage={submitError.message}
          httpCodeMessages={{
            400: '{message}', // Use the API-supplied message
          }} />
      }

      <Row>
        <Col>
          <TextInput
            label="Name"
            validationRules={[required, minLength(1), maxLength(50)]}
            name="name" />

          <TextInput
            label="Address"
            validationRules={[required, minLength(1), maxLength(50)]}
            name="address" />

          <TextInput
            label="City"
            validationRules={[required, minLength(1), maxLength(50)]}
            name="city" />

          <TextInput
            label="County"
            validationRules={[required, minLength(1), maxLength(50)]}
            name="county" />

          <States
            includeEmpty={true}
            abbreviations={true}
            name="state"
            label="State"
            includeCanada={false}
            validationRules={[required]} />

          <TextInput
            label="Zip"
            validationRules={[required, numeric, minLength(5), maxLength(5)]}
            name="zip" />

          <Select name="timezone" label="Timezone">
            <option value=""></option>
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Phoenix">Arizona</option>
            <option value="America/Los_Angeles">Pacific</option>
          </Select>

          <Select name="ratePlanId" label="Rate Plan">
            <option value=""></option>
            {(ratePlans.result?.data || []).map((ratePlan) =>
              <option value={ratePlan.id} key={`rate-plan-${ratePlan.id}`}>{ratePlan.name}</option>
            )}
          </Select>

          <Select name="serviceRatePlanId" label="Service Rate Plan">
            <option value=""></option>
            {(serviceRatePlans.result || []).map((ratePlan) =>
              <option value={ratePlan.id} key={`service-rate-plan-${ratePlan.id}`}>{ratePlan.name}</option>
            )}
          </Select>
        </Col>

        <Col>
          {user.hasPermission('facility_manage_geographical') &&
            <TextInput
              label="Latitude"
              validationRules={[maxLength(15)]}
              name="latitude" />
          }

          {user.hasPermission('facility_manage_geographical') &&
            <TextInput
              label="Longitude"
              validationRules={[maxLength(15)]}
              name="longitude" />
          }

          <TextInput
            label="External System ID"
            validationRules={[maxLength(25)]}
            name="externalId" />

          <Select name="inmateImportMode" label="Import Mode">
            <option value="null">N/A</option>
            <option value="ITDS">ITDS</option>
            <option value="Generic">Generic</option>
            <option value="GenericNumericTransformAccount">GenericNumericTransformAccount</option>
            <option value="JailTracker">JailTracker</option>
            <option value="TurnKey">TurnKey</option>
            <option value="BluHorse">BluHorse</option>
            <option value="CPC">CPC</option>
            <option value="Shieldware">Shieldware</option>
          </Select>

          <Select name="itdsMode" label="ITDS Mode">
            <option value="0">Disabled</option>
            <option value="1">Legacy</option>
            <option value="2">Full</option>
          </Select>

          <Select name="facilityType" label="Facility Type">
            <option></option>
            {config.facilityTypes.map((type) =>
              <option value={type} key={`facility-type-${type}`}>{type}</option>
            )}
          </Select>
        </Col>

        <Col>
          <FeatureCheckboxes form={props.form} facility={props.facility} />
        </Col>

        <Col>
          <Card>
            <Card.Header>On/Off Statuses</Card.Header>
            <Card.Body>
              <Checkbox
                name="communicationsEnabled"
                type="switch"
                onChange={() => {
                  form.mutators.disableCommunicationOptions();
                }}
                label="Communications" />

              <div style={{ paddingLeft: 10 }}>
                <Checkbox
                  name="featureTexting"
                  type="switch"
                  disabled={!values.communicationsEnabled}
                  label="Texting" />

                <Checkbox
                  name="textingLocalStatus"
                  type="switch"
                  disabled={!values.communicationsEnabled}
                  label="Texting Facility Status" />

                <Checkbox
                  name="audioCallingEnabled"
                  type="switch"
                  disabled={!values.communicationsEnabled}
                  label="Audio Calling" />

                <Checkbox
                  name="videoCallingEnabled"
                  type="switch"
                  disabled={!values.communicationsEnabled}
                  label="Video Calling" />
              </div>

              <Checkbox
                name="featureCommissaryTransfer"
                type="switch"
                label="Purchase Feature" />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AddEditForm;
